/**
 * SpacePricingTable - Muestra tarifas del espacio para los próximos 6 meses
 */
import React, { useMemo } from 'react'
import { useTheme } from '../../hooks/useTheme'
import { useDisponibilidadEspacio } from '../../hooks/useEspacios'
import { formatCurrency } from '../../utils/currencyHelpers'
import LoadingSpinner from '../common/LoadingSpinner'

interface SpacePricingTableProps {
  espacioId: string
  unidadReserva: string
}

const SpacePricingTable: React.FC<SpacePricingTableProps> = ({
  espacioId,
  unidadReserva
}) => {
  const { colors } = useTheme()

  // Calcular rango de 6 meses
  const rangoFechas = useMemo(() => {
    const hoy = new Date()
    const inicio = new Date(hoy.getFullYear(), hoy.getMonth(), 1) // Primer día del mes actual
    const fin = new Date(hoy.getFullYear(), hoy.getMonth() + 6, 0) // Último día del mes +6

    return {
      fecha_entrada: inicio.toISOString().split('T')[0],
      fecha_salida: fin.toISOString().split('T')[0]
    }
  }, [])

  // Usar el mismo hook que DateAvailabilityModal
  const { data, isLoading } = useDisponibilidadEspacio(espacioId, rangoFechas)

  // Extraer tarifas únicas del desglose (EXACTAMENTE como DateAvailabilityModal líneas 587-606)
  const tarifasUnicas = useMemo(() => {
    if (!data?.desglose) return []

    const tarifasMap = new Map<string, {
      nombre_simple: string
      tarifa_simple: number
      nombre_multiple: string | null
      tarifa_multiple: number | null
    }>()

    data.desglose.forEach(dia => {
      const key = `${dia.nombre_tarifa_simple}|${dia.tarifa_simple}`
      if (!tarifasMap.has(key)) {
        tarifasMap.set(key, {
          nombre_simple: dia.nombre_tarifa_simple || 'Tarifa',
          tarifa_simple: dia.tarifa_simple || 0,
          nombre_multiple: dia.nombre_tarifa_multiple,
          tarifa_multiple: dia.tarifa_multiple
        })
      }
    })

    return Array.from(tarifasMap.values())
  }, [data])

  if (isLoading) {
    return (
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-xs text-blue-500 italic">Cargando tarifas...</p>
      </div>
    )
  }

  if (!tarifasUnicas.length) {
    return (
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-700 font-medium">💰 Tarifas</p>
        <p className="text-xs text-blue-500 italic mt-2">No hay información de tarifas disponible</p>
      </div>
    )
  }

  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 w-full md:max-w-md">
      <p className="text-sm text-blue-700 font-medium">💰 Tarifas disponibles:</p>
      <div className="space-y-1 mt-2">
        {tarifasUnicas.slice(0, 2).map((tarifa, idx) => (
          <div key={idx} className="text-xs text-blue-700 flex justify-between">
            <span>{tarifa.nombre_simple}</span>
            <span className="font-semibold">
              {formatCurrency(tarifa.tarifa_simple)}{tarifa.tarifa_multiple ? `/${formatCurrency(tarifa.tarifa_multiple)}` : ''}
            </span>
          </div>
        ))}
        {tarifasUnicas.length > 2 && (
          <p className="text-xs text-blue-500 italic">+{tarifasUnicas.length - 2} más</p>
        )}
      </div>
      <p className="text-xs text-blue-600 mt-2">Cada día muestra su tarifa</p>
    </div>
  )
}

export default SpacePricingTable
